import * as React from "react"
import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Search, Edit, Database, ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react"

export interface ResourceInstance {
  id: string
  instanceNumber: number
  instanceName: string
  instanceDescription: string
  resourceTypeNumber: number
  resourceTypeName: string
  // Raw API record, preserved for the inline edit flow (carries namespace,
  // resourceCategory, parentInstancePaths that the table shape drops)
  raw: any
}

export interface ResourceInstanceListProps {
  onAdd?: () => void
  onEdit?: (instance: ResourceInstance) => void
}

export function ResourceInstanceList({ onAdd, onEdit }: ResourceInstanceListProps = {}) {
  const [searchQuery, setSearchQuery] = React.useState("")
  const [currentPage, setCurrentPage] = React.useState(1)
  const itemsPerPage = 10

  const { data, isLoading, isError, refetch } = useApiQuery(
    "getResourceInstances",
    {}
  )
  const { data: resourceTypesData } = useApiQuery("getResourceTypes", {})
  const resourceTypeMap = React.useMemo(() => {
    const types = (resourceTypesData as any)?.content?.resourceTypes || (resourceTypesData as any)?.resourceTypes || []
    return new Map(types.map((rt: any) => [rt.resourceTypeNumber, rt.resourceTypeName]))
  }, [resourceTypesData])

  const instances: ResourceInstance[] = ((data as any)?.content?.resourceInstances || (data as any)?.resourceInstances || []).map((inst: any) => ({
    id: inst.id || '',
    instanceNumber: inst.instanceNumber || 0,
    instanceName: inst.resourceInstanceName || '',
    instanceDescription: inst.resourceInstanceDescription || '',
    resourceTypeNumber: inst.resourceTypeNumber || 0,
    resourceTypeName: (resourceTypeMap.get(inst.resourceTypeNumber) as string) || `Type #${inst.resourceTypeNumber}`,
    raw: inst,
  }))

  const filteredInstances = instances.filter((inst) =>
    inst.instanceName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    inst.instanceDescription?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const totalPages = Math.ceil(filteredInstances.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedInstances = filteredInstances.slice(startIndex, startIndex + itemsPerPage)

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load resource instances</p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Database className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Manage Resource Instances</CardTitle>
                <CardDescription className="text-base">Resource instances grouped by type</CardDescription>
              </div>
            </div>
            <Button size="sm" className="gap-2" onClick={() => onAdd?.()}>
              <Plus className="h-4 w-4" />
              Add Resource Instance
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search instances..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1)
                }}
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <Table className="text-base">
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="font-semibold text-base">Name</TableHead>
                <TableHead className="font-semibold text-base">Resource Type</TableHead>
                <TableHead className="w-[100px] font-semibold text-base">ID</TableHead>
                <TableHead className="font-semibold text-base">Description</TableHead>
                <TableHead className="w-[100px] text-right font-semibold text-base">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedInstances.length > 0 ? (
                paginatedInstances.map((instance) => (
                  <TableRow key={instance.instanceNumber} className="transition-colors hover:bg-muted/50">
                    <TableCell className="font-medium text-base">
                      <Link
                        to="/rema/resource-instances/$instanceNumber"
                        params={{ instanceNumber: String(instance.instanceNumber) }}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {instance.instanceName}
                      </Link>
                    </TableCell>
                    <TableCell className="font-medium text-base">
                      {instance.resourceTypeName}
                    </TableCell>
                    <TableCell className="font-medium text-base">
                      {instance.instanceNumber}
                    </TableCell>
                    <TableCell className="text-base text-muted-foreground">
                      {instance.instanceDescription || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                          <Link
                            to="/rema/resource-instances/$instanceNumber/edit"
                            params={{ instanceNumber: String(instance.instanceNumber) }}
                          >
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit {instance.instanceName}</span>
                          </Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                    No resource instances found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t py-3">
              <p className="text-base text-muted-foreground">
                Showing {startIndex + 1} to{" "}
                {Math.min(startIndex + itemsPerPage, filteredInstances.length)} of{" "}
                {filteredInstances.length} instances
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">Page {currentPage} of {totalPages}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
